import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface InlineDocViewerProps {
  filePath: string;
  fileName: string;
  trigger?: React.ReactNode;
}

const InlineDocViewer = ({ filePath, fileName, trigger }: InlineDocViewerProps) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName || filePath);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    const { data } = await supabase.storage
      .from("application-documents")
      .createSignedUrl(filePath, 3600);
    setUrl(data?.signedUrl || null);
    setLoading(false);
  };

  return (
    <>
      {trigger ? (
        <span onClick={handleOpen}>{trigger}</span>
      ) : (
        <Button
          size="sm"
          variant="outline"
          title="View Document"
          className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
          onClick={handleOpen}
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-sm truncate">{fileName}</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : url ? (
            isImage ? (
              <div className="flex items-center justify-center w-full h-[70vh] rounded-md border bg-muted/20 overflow-auto">
                <img src={url} alt={fileName} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <iframe
                src={`${url}#toolbar=0&navpanes=0&view=Fit`}
                className="w-full h-[70vh] rounded-md border"
                title={fileName}
              />
            )
          ) : (
            <p className="text-center text-muted-foreground py-8">Failed to load document</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InlineDocViewer;
